import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { ContactRow } from "./ContactBits";

export default function ModernTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        color: "#0a0a0a",
        background: "#ffffff",
        padding: "48px 56px",
        fontSize: 10,
        lineHeight: 1.5,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <header style={{ borderBottom: "2px solid #0a0a0a", paddingBottom: 14, marginBottom: 18 }}>
        <div data-resume-h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: "-0.02em", lineHeight: 1 }}>
          {personal.fullName || "Your Name"}
        </div>
        {personal.title && (
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#2400FF", marginTop: 6 }}>
            {personal.title}
          </div>
        )}
        <ContactRow
          personal={personal}
          size={10}
          color="#525252"
          separator="·"
          style={{ marginTop: 10, fontSize: 9.5, gap: 10 }}
        />
      </header>

      {summary && (
        <Section title="Professional Summary">
          <div style={{ color: "#171717" }}>{summary}</div>
        </Section>
      )}

      {skills?.length > 0 && (
        <Section title="Skills">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {skills.map((s, i) => (
              <span key={`${s}-${i}`} style={{ border: "1px solid rgba(0,0,0,0.15)", padding: "2px 8px", fontSize: 9.5 }}>{s}</span>
            ))}
          </div>
        </Section>
      )}

      {hasAny(experience, ["company", "role"]) && (
        <Section title="Experience">
          {experience.map((e, i) => {
            const bullets = nonEmptyBullets(e.bullets);
            if (!e.company && !e.role && bullets.length === 0) return null;
            return (
              <div key={e.id || i} data-page-item="true" style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <span data-resume-jobtitle style={{ fontWeight: 600, color: "#0a0a0a" }}>{e.role}</span>
                    {e.role && e.company && <span style={{ color: "#525252" }}> · </span>}
                    <span data-resume-company style={{ color: "#525252" }}>{e.company}</span>
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373", letterSpacing: "0.05em" }}>
                    <span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span>
                    {e.location ? <> · <span data-resume-location>{e.location}</span></> : ""}
                  </div>
                </div>
                {bullets.length > 0 && (
                  <ul data-resume-workdesc style={{ margin: "4px 0 0 14px", padding: 0, listStyle: "disc" }}>
                    {bullets.map((b, bi) => (
                      <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2 }}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(projects, ["name"]) && (
        <Section title="Projects">
          {projects.map((p, i) => {
            if (!p.name) return null;
            return (
              <div key={p.id || i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <span data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</span>
                    {p.link && <span style={{ color: "#525252" }}> · {p.link}</span>}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373" }}>{p.tech}</div>
                </div>
                {p.description && <div style={{ marginTop: 2 }}>{p.description}</div>}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(education, ["school", "degree"]) && (
        <Section title="Education">
          {education.map((ed, i) => {
            if (!ed.school && !ed.degree) return null;
            return (
              <div key={ed.id || i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{ed.school}</span>
                    {ed.degree && <span style={{ color: "#525252" }}> · {ed.degree}{ed.field ? `, ${ed.field}` : ""}</span>}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373" }}>
                    {fmtRange(ed.startDate, ed.endDate)}
                  </div>
                </div>
                {ed.details && <div style={{ color: "#525252", marginTop: 2 }}>{ed.details}</div>}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(certifications, ["name"]) && (
        <Section title="Certifications">
          {certifications.map((c, i) => {
            if (!c.name) return null;
            return (
              <div key={c.id || i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{c.name}</span>
                  {c.issuer && <span style={{ color: "#525252" }}> · {c.issuer}</span>}
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373" }}>{c.date}</div>
              </div>
            );
          })}
        </Section>
      )}

      {languages?.some((l) => l.name?.trim()) && (
        <Section title="Languages">
          {languages.map((l) => l.name?.trim() && (
            <div key={l.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontWeight: 600 }}>{l.name}</span>
              {l.level && (
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, color: "#737373" }}>{l.level}</span>
              )}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 16 }}>
      <div data-resume-h2 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: "#2400FF", marginBottom: 8, borderBottom: "1px solid rgba(0,0,0,0.08)", paddingBottom: 4 }}>
        {title}
      </div>
      <div>{children}</div>
    </section>
  );
}
